import { formatRecordType } from "../../utils/documentUtils";

/**
 * Display-formatting helpers for `TransformationHeader`.
 *
 * Backs the friendly labels reviewers see on the Mapping tab, so raw enum
 * values like `structured_parse` / `eor` / `46ms` don't leak through to the
 * UI. Each helper falls back to a sensible default for unknown inputs so a
 * new backend enum value still renders something readable rather than
 * disappearing.
 */

/** Friendly labels for `processing_metadata.extraction_method`. Keys match
 *  the `ExtractionMethod` type. Unknown values fall through to a
 *  title-cased version of the raw enum. */
const EXTRACTION_METHOD_LABELS: Record<string, string> = {
  ocr_plus_ai: "OCR + AI",
  structured_parse: "Structured parse",
  native_text: "Native text",
  hybrid: "Hybrid",
};

export const formatExtractionMethod = (method: string | undefined | null): string | null => {
  if (!method) return null;
  return EXTRACTION_METHOD_LABELS[method] ?? formatRecordType(method);
};

/** Friendly labels for `processing_metadata.document_type`. Keys match the
 *  `DocumentTransformationType` enum where known; additional aliases handle
 *  values the backend may emit outside the enum (e.g. `eor`). Unknown
 *  values fall through to title-cased text with simple acronym handling. */
const DOCUMENT_SUBTYPE_LABELS: Record<string, string> = {
  "50_percent_report": "50% Report",
  medical_stoploss_claims: "Medical Stop-Loss Claims",
  pharmacy_stoploss_claims: "Pharmacy Stop-Loss Claims",
  stoploss_claims: "Stop-Loss Claims",
  policy_document: "Policy Document",
  reimbursements: "Reimbursements",
  unknown_format: "Unknown Format",
  eor: "EOR",
  eor_document: "EOR Document",
};

/** Acronyms whose casing should be preserved when falling back from the
 *  label map. Lowercase keys; uppercase values. */
const ACRONYMS: Record<string, string> = {
  eor: "EOR",
  pdf: "PDF",
  pbm: "PBM",
  tpa: "TPA",
  rx: "Rx",
  icd: "ICD",
  cpt: "CPT",
  ndc: "NDC",
  ach: "ACH",
};

export const formatDocumentSubtype = (subtype: string | undefined | null): string | null => {
  if (!subtype) return null;
  const labeled = DOCUMENT_SUBTYPE_LABELS[subtype];
  if (labeled) return labeled;
  // Fallback: title-case + acronym fix. Splits on `_`, title-cases each part,
  // overrides with ACRONYMS where applicable so `eor_report` → `EOR Report`.
  return subtype
    .split("_")
    .map((word) => ACRONYMS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Format processing time for display. The existing `formatMilliseconds`
 * floors to integer seconds, which renders sub-second processing as "0s" —
 * misleading for fast docs. This helper returns:
 *   - `"Under 1 second"` for values 0–999 ms (true minimum since we don't
 *     surface sub-second precision elsewhere)
 *   - `"Xs"` for 1–59 s
 *   - `"Xm Ys"` for ≥ 60 s
 *
 * Returns `null` for null/undefined inputs so callers can skip the row
 * cleanly.
 */
export const formatProcessingTime = (ms: number | undefined | null): string | null => {
  if (ms === null || ms === undefined) return null;
  if (ms < 1000) return "Under 1 second";
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return remainingSeconds === 0 ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
};
